import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../utils/constants';

const C = {
  bg:        '#080d18',
  nav:       '#0a1020',
  surface:   '#0f172a',
  surface2:  '#111d35',
  border:    '#1e3a5f',
  borderDim: '#172035',
  teal:      '#22d3ee',
  blue:      '#6366f1',
  text:      '#e2e8f0',
  muted:     '#64748b',
  critical:  '#ef4444',
  high:      '#f59e0b',
  low:       '#10b981',
  white:     '#ffffff',
};

const GRAD = `linear-gradient(135deg, ${C.teal} 0%, ${C.blue} 100%)`;

const gradBtn = (extra = {}) => ({
  background: GRAD,
  color: C.white,
  border: 'none',
  borderRadius: 10,
  padding: '14px 36px',
  cursor: 'pointer',
  fontWeight: 700,
  fontSize: 15,
  fontFamily: 'Inter, sans-serif',
  boxShadow: '0 0 32px rgba(34,211,238,0.22)',
  transition: 'opacity 0.2s',
  ...extra,
});

const ghostBtn = (extra = {}) => ({
  background: 'transparent',
  color: C.muted,
  border: `1px solid ${C.border}`,
  borderRadius: 10,
  padding: '9px 20px',
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: 13,
  fontFamily: 'Inter, sans-serif',
  ...extra,
});

const FEATURES = [
  { icon: '🔍', title: 'Daily Automated Scans',                      desc: 'Your entire AWS environment scanned every day at 06:00 UTC. Security groups, IAM, S3, CloudTrail, EBS — all checked automatically.' },
  { icon: '💰', title: 'Cost Optimisation Engine',                   desc: 'Detects overprovisioned EC2 and Lambda, unused instances, missing lifecycle policies, and Savings Plan opportunities. Average find: $800–$2,400/mo.' },
  { icon: '🛡️', title: 'Security Findings and Threat Detection',     desc: 'Critical-to-low findings across open security groups, public S3 buckets, stale IAM keys, unencrypted EBS, missing MFA, and more.' },
  { icon: '📄', title: 'Executive PDF Reports',                      desc: 'Board-ready PDF on every scan — security score, breach cost exposure, compliance posture, and a prioritised remediation roadmap.' },
  { icon: '✅', title: 'Compliance — GDPR, SOC 2, HIPAA, PCI DSS',  desc: "Live compliance status derived from your actual findings. Know exactly what's blocking certification before your auditor does." },
  { icon: '🔔', title: 'Slack and Email Alerts',                     desc: 'Get notified the moment critical findings are detected. Every scan triggers an email summary — connect Slack for instant team alerts.' },
  { icon: '📊', title: 'Up to 12-Month Scan History',               desc: 'Track your security score and cost savings over time. Show stakeholders the trend, not just a snapshot.' },
  { icon: '⚡', title: '2-Minute CloudFormation Setup',             desc: "No agents, no code changes, no credentials stored. One CloudFormation stack and you're scanning within minutes." },
];

const COMPETITORS = [
  { name: 'Spectraine',         price: '$497/mo',      note: 'Full stack: security + cost + compliance', highlight: true },
  { name: 'Wiz',                price: '$25,000+/yr',  note: 'Enterprise only, 6-month sales cycle' },
  { name: 'Datadog Cloud SIEM', price: '$20,000+/yr',  note: 'Enterprise only, complex setup' },
  { name: 'Lacework',           price: '$15,000+/yr',  note: 'Enterprise only, agent-based' },
  { name: 'DIY (engineer hrs)', price: '$1,500–$3,000/mo', note: 'Manual, inconsistent, no reporting' },
];

const OBJECTIONS = [
  {
    q: "Why not a cheaper tool?",
    a: "Cheap tools get ignored. At this price, Spectraine is a line item your CTO and CFO will hold accountable. The first scan typically finds $800–$2,400/mo in AWS waste alone — that's a 1.6–4.8x ROI before you touch a single security finding.",
  },
  {
    q: "We already have AWS Trusted Advisor.",
    a: "Trusted Advisor gives you a checklist. Spectraine gives you a security score, breach cost exposure estimate, compliance posture mapped to GDPR/SOC 2/HIPAA, and a PDF you can hand to your board — automatically, every day.",
  },
  {
    q: "Is this just another SaaS dashboard?",
    a: "No agents. No code changes. No credentials stored on our end. You deploy one read-only IAM role via CloudFormation and we scan from the outside. Setup takes 2 minutes.",
  },
  {
    q: "What if we don't find enough savings to justify it?",
    a: "Cancel anytime — no contracts, no minimum term. If you're spending $8k+/mo on AWS, finding even 6% waste more than covers the subscription.",
  },
];

const PricingPage = () => {
  const navigate       = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [openQ,   setOpenQ]   = useState(null);

  const handleSubscribe = async () => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/'); return; }
    setLoading(true);
    setError('');
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const form = new FormData();
      form.append('email',     user.email     || '');
      form.append('company',   user.company   || '');
      form.append('full_name', user.full_name  || '');
      const res  = await fetch(`${API_URL}/subscribe`, { method: 'POST', body: form });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Subscription failed');
      window.location.href = data.checkout_url;
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const glow = (w, h, top, left, col) => ({
    position: 'absolute', borderRadius: '50%', filter: 'blur(90px)',
    pointerEvents: 'none', width: w, height: h, top, left,
    background: col, zIndex: 0,
  });

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.text, fontFamily: 'Inter, -apple-system, sans-serif', overflowX: 'hidden' }}>

      {/* NAV */}
      <nav style={{ background: `${C.nav}cc`, borderBottom: `1px solid ${C.border}`, padding: '0 40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 58, position: 'sticky', top: 0, zIndex: 100, backdropFilter: 'blur(14px)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M12 2L3 7v5c0 5.25 3.75 10.15 9 11.35C17.25 22.15 21 17.25 21 12V7L12 2z" stroke={C.teal} strokeWidth="2" strokeLinejoin="round"/>
            <path d="M9 12l2 2 4-4" stroke={C.teal} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span style={{ fontWeight: 800, fontSize: 16, letterSpacing: '0.06em', background: GRAD, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>SPECTRAINE</span>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button onClick={() => navigate('/')} style={ghostBtn()}>Log In</button>
          <button onClick={handleSubscribe} disabled={loading} style={gradBtn({ padding: '9px 20px', fontSize: 13 })}>
            {loading ? 'Redirecting…' : 'Get Started'}
          </button>
        </div>
      </nav>

      {/* HERO */}
      <section style={{ position: 'relative', textAlign: 'center', padding: '88px 24px 72px', overflow: 'hidden' }}>
        <div style={glow(700, 500, -200, '50%', `${C.teal}07`)} />
        <div style={glow(400, 400, 0, -100, `${C.blue}07`)} />

        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: `${C.teal}14`, border: `1px solid ${C.teal}35`, borderRadius: 20, padding: '4px 16px', fontSize: 11, fontWeight: 700, color: C.teal, letterSpacing: '0.08em', marginBottom: 24, position: 'relative', zIndex: 1 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.teal, display: 'inline-block' }} />
          AWS SECURITY · COST INTELLIGENCE · COMPLIANCE
        </div>

        <h1 style={{ margin: '0 auto 20px', maxWidth: 720, fontSize: 'clamp(34px, 5.5vw, 56px)', fontWeight: 900, color: C.white, lineHeight: 1.08, letterSpacing: '-0.02em', position: 'relative', zIndex: 1 }}>
          Your AWS bill is hiding{' '}
          <span style={{ background: GRAD, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>money and risk.</span>
          <br />We surface both. Daily.
        </h1>

        <p style={{ margin: '0 auto 36px', maxWidth: 540, color: C.muted, fontSize: 16, lineHeight: 1.8, position: 'relative', zIndex: 1 }}>
          Spectraine scans your entire AWS environment every day — finding security vulnerabilities,
          compliance gaps, and cost waste your team doesn't have time to hunt manually.
        </p>

        <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 16, position: 'relative', zIndex: 1 }}>
          <button onClick={handleSubscribe} disabled={loading} style={gradBtn({ fontSize: 16, padding: '15px 44px' })}>
            {loading ? 'Redirecting to Stripe…' : 'Start Protecting Your AWS — $497/mo'}
          </button>
        </div>

        <p style={{ color: C.muted, fontSize: 13, margin: 0, position: 'relative', zIndex: 1 }}>
          Cancel anytime · No contracts · No agents · 2-minute setup
        </p>

        {error && (
          <div style={{ marginTop: 16, display: 'inline-block', background: `${C.critical}15`, border: `1px solid ${C.critical}40`, borderRadius: 8, padding: '10px 20px', color: C.critical, fontSize: 13 }}>
            {error}
          </div>
        )}
      </section>

      {/* STATS STRIP */}
      <section style={{ background: C.surface, borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, padding: '28px 40px' }}>
        <div style={{ maxWidth: 960, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 32, textAlign: 'center' }}>
          {[
            { stat: '$800–$2,400', label: 'avg. monthly savings found',           color: C.low  },
            { stat: '2 min',       label: 'setup via CloudFormation',             color: C.teal },
            { stat: '8+ checks',   label: 'across security, cost and compliance', color: C.blue },
            { stat: 'Every day',   label: 'automated scan at 06:00 UTC',          color: C.high },
          ].map(({ stat, label, color }) => (
            <div key={label}>
              <div style={{ fontSize: 28, fontWeight: 900, color, marginBottom: 4 }}>{stat}</div>
              <div style={{ fontSize: 13, color: C.muted }}>{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING + VALUE */}
      <section style={{ position: 'relative', padding: '80px 24px', overflow: 'hidden' }}>
        <div style={glow(500, 500, -100, '60%', `${C.blue}06`)} />

        <div style={{ maxWidth: 980, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40, alignItems: 'start' }}>

          {/* Pricing card */}
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 24, overflow: 'hidden', boxShadow: '0 0 60px rgba(34,211,238,0.07), 0 20px 56px rgba(0,0,0,0.4)', position: 'relative', zIndex: 1 }}>
            <div style={{ height: 3, background: GRAD }} />
            <div style={{ padding: '36px 40px' }}>

              <div style={{ display: 'inline-block', background: `${C.teal}18`, border: `1px solid ${C.teal}40`, borderRadius: 20, padding: '3px 14px', fontSize: 11, fontWeight: 700, color: C.teal, letterSpacing: '0.08em', marginBottom: 20 }}>
                PROFESSIONAL PLAN
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, marginBottom: 6 }}>
                <span style={{ fontSize: 54, fontWeight: 900, color: C.white, lineHeight: 1 }}>$497</span>
                <span style={{ color: C.muted, fontSize: 16, marginBottom: 9 }}>/mo</span>
              </div>
              <p style={{ margin: '0 0 20px', color: C.muted, fontSize: 13 }}>Cancel anytime · No contracts · No setup fees</p>

              <div style={{ background: `${C.low}12`, border: `1px solid ${C.low}30`, borderRadius: 10, padding: '12px 16px', marginBottom: 28 }}>
                <span style={{ fontSize: 13, color: C.low, fontWeight: 600 }}>💰 Typical first-scan ROI: 1.6–4.8x</span>
                <span style={{ fontSize: 12, color: C.muted, display: 'block', marginTop: 2 }}>Average customer finds $800–$2,400/mo in AWS waste</span>
              </div>

              <button onClick={handleSubscribe} disabled={loading} style={gradBtn({ width: '100%', fontSize: 15, padding: '14px', marginBottom: 28 })}>
                {loading ? 'Redirecting to Stripe…' : 'Get Started Now'}
              </button>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {FEATURES.map(f => (
                  <div key={f.title} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <span style={{ fontSize: 15, flexShrink: 0, marginTop: 1 }}>{f.icon}</span>
                    <div>
                      <div style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>{f.title}</div>
                      <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, marginTop: 2 }}>{f.desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 24, paddingTop: 20, borderTop: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 14 }}>🔒</span>
                <span style={{ fontSize: 12, color: C.muted }}>Secure payment via Stripe. We never store your AWS credentials.</span>
              </div>
            </div>
          </div>

          {/* Value justification */}
          <div style={{ position: 'relative', zIndex: 1 }}>
            <h2 style={{ margin: '0 0 10px', fontSize: 26, fontWeight: 800, color: C.white, lineHeight: 1.2 }}>The maths are simple.</h2>
            <p style={{ margin: '0 0 28px', color: C.muted, fontSize: 15, lineHeight: 1.8 }}>
              If you're spending $8k+/mo on AWS, finding even 6% waste pays for Spectraine.
              Most customers find far more — and that's before factoring in the cost of a single security incident.
            </p>

            <div style={{ background: `${C.critical}0d`, border: `1px solid ${C.critical}30`, borderLeft: `3px solid ${C.critical}`, borderRadius: 12, padding: '18px 20px', marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: C.critical, fontWeight: 700, letterSpacing: '0.07em', marginBottom: 10 }}>⚠️ WHAT A BREACH ACTUALLY COSTS</div>
              {[
                ['Open security group', '$50k–$200k exposure'],
                ['Public S3 bucket',    '$50k–$150k exposure'],
                ['Root MFA disabled',   '$50k–$200k exposure'],
                ['CloudTrail disabled', '$10k–$50k exposure'],
              ].map(([risk, cost]) => (
                <div key={risk} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '5px 0', borderBottom: `1px solid ${C.borderDim}` }}>
                  <span style={{ color: C.muted }}>{risk}</span>
                  <span style={{ color: C.critical, fontWeight: 700 }}>{cost}</span>
                </div>
              ))}
              <div style={{ marginTop: 10, fontSize: 12, color: C.muted }}>Spectraine detects all of the above on day one.</div>
            </div>

            <div style={{ background: C.surface2, border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.teal}`, borderRadius: 12, padding: '18px 20px', marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: C.teal, fontWeight: 700, letterSpacing: '0.07em', marginBottom: 10 }}>⏱️ VS. DOING THIS MANUALLY</div>
              {[
                ['Engineer time to replicate this', '10–20 hrs/mo'],
                ['At $150/hr loaded cost',          '$1,500–$3,000/mo'],
                ['Spectraine',                      '$497/mo'],
              ].map(([label, val], i) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 0', borderBottom: i < 2 ? `1px solid ${C.borderDim}` : 'none' }}>
                  <span style={{ color: C.muted }}>{label}</span>
                  <span style={{ color: i === 2 ? C.teal : C.text, fontWeight: i === 2 ? 700 : 400 }}>{val}</span>
                </div>
              ))}
            </div>

            <div style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 12, padding: '18px 20px' }}>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: '0.07em', marginBottom: 10 }}>VS. ENTERPRISE ALTERNATIVES</div>
              {COMPETITORS.map((row, i) => (
                <div key={row.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: i < COMPETITORS.length - 1 ? `1px solid ${C.borderDim}` : 'none' }}>
                  <div>
                    <span style={{ fontSize: 13, color: row.highlight ? C.white : C.muted, fontWeight: row.highlight ? 700 : 400 }}>{row.name}</span>
                    <span style={{ fontSize: 11, color: C.muted, display: 'block' }}>{row.note}</span>
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: row.highlight ? C.low : C.muted, flexShrink: 0, marginLeft: 12 }}>{row.price}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ padding: '64px 24px 80px', background: C.surface, borderTop: `1px solid ${C.border}` }}>
        <div style={{ maxWidth: 680, margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', margin: '0 0 40px', fontSize: 26, fontWeight: 800, color: C.white }}>Straight answers to hard questions</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {OBJECTIONS.map((item, i) => (
              <div key={i} style={{ background: C.surface2, border: `1px solid ${openQ === i ? C.teal + '60' : C.border}`, borderRadius: 12, overflow: 'hidden', transition: 'border-color 0.2s' }}>
                <button onClick={() => setOpenQ(openQ === i ? null : i)} style={{ width: '100%', background: 'none', border: 'none', padding: '18px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: C.white, textAlign: 'left' }}>{item.q}</span>
                  <span style={{ color: C.teal, fontSize: 20, flexShrink: 0, marginLeft: 16, display: 'inline-block', transition: 'transform 0.2s', transform: openQ === i ? 'rotate(45deg)' : 'rotate(0deg)' }}>+</span>
                </button>
                {openQ === i && (
                  <div style={{ padding: '0 20px 18px' }}>
                    <p style={{ margin: 0, fontSize: 14, color: C.muted, lineHeight: 1.8 }}>{item.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section style={{ position: 'relative', padding: '80px 24px', textAlign: 'center', overflow: 'hidden' }}>
        <div style={glow(600, 400, -100, '30%', `${C.teal}07`)} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <h2 style={{ margin: '0 0 14px', fontSize: 'clamp(26px, 4vw, 40px)', fontWeight: 900, color: C.white, lineHeight: 1.15 }}>
            Your next AWS bill could be{' '}
            <span style={{ background: GRAD, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>hundreds less.</span>
            <br />Find out today.
          </h2>
          <p style={{ margin: '0 auto 32px', maxWidth: 460, color: C.muted, fontSize: 15, lineHeight: 1.75 }}>
            Setup takes 2 minutes. Your first scan runs immediately.
            The PDF report lands in your dashboard before your next meeting.
          </p>
          <button onClick={handleSubscribe} disabled={loading} style={gradBtn({ fontSize: 16, padding: '15px 48px' })}>
            {loading ? 'Redirecting…' : 'Start Now — $497/mo'}
          </button>
          <p style={{ marginTop: 16, color: C.muted, fontSize: 13 }}>
            No contract. Cancel anytime.{' '}
            <a href="mailto:andrei@spectraine.com" style={{ color: C.teal, textDecoration: 'none', fontWeight: 600 }}>
              Questions? andrei@spectraine.com
            </a>
          </p>
        </div>
      </section>

    </div>
  );
};

export default PricingPage;
